import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Search } from "lucide-react";

/**
 * Combobox — desplegable con búsqueda por texto.
 * - value: string seleccionado
 * - onChange: (v: string) => void
 * - options: string[]
 * - placeholder: texto cuando no hay valor
 * - emptyText: texto cuando no hay coincidencias tras filtrar
 * - dataTestid: base para data-testid del input y los items
 *
 * Comportamiento: el usuario escribe → se filtra la lista → debe pulsar en
 * una opción del desplegable para confirmar el valor.
 */
export function Combobox({ value, onChange, options, placeholder = "Selecciona…", emptyText = "Sin resultados", dataTestid = "combobox" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  // Cierra el desplegable al hacer clic fuera
  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Al abrir, asegura que el combobox esté visible dentro del viewport para
  // que el dropdown no se corte por abajo (sobre todo en móvil).
  useEffect(() => {
    if (open && wrapRef.current) {
      const t = setTimeout(() => {
        try {
          wrapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch { /* ignore */ }
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filtered = query.trim()
    ? options.filter((o) => o.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 60)
    : options.slice(0, 60);

  const selectOption = (opt) => {
    onChange(opt);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid={`${dataTestid}-toggle`}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 bg-transparent border border-[#5B3E2E]/20 rounded-xl px-3 py-2 text-left text-[#5B3E2E] hover:border-[#5B3E2E]/30 focus:outline-none focus:ring-1 focus:ring-[#E48D57] transition-colors"
      >
        <span className={`truncate text-sm ${value ? "" : "text-[#5B3E2E]/40"}`}>
          {value || placeholder}
        </span>
        <ChevronDown size={14} className={`text-[#5B3E2E]/40 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-[#FDFAF4] border border-[#5B3E2E]/15 rounded-xl shadow-[0_10px_30px_rgba(91,62,46,0.15)] overflow-hidden animate-fade-in">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#5B3E2E]/8">
            <Search size={14} className="text-[#5B3E2E]/40 flex-shrink-0" />
            <input
              data-testid={`${dataTestid}-search`}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Escribe para buscar…"
              className="flex-1 bg-transparent outline-none text-sm text-[#5B3E2E] placeholder:text-[#5B3E2E]/35"
              style={{ fontSize: "16px" }}
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-[#5B3E2E]/40 text-center py-4 px-3">{emptyText}</p>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  data-testid={`${dataTestid}-option-${opt}`}
                  onClick={() => selectOption(opt)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left text-[#5B3E2E] hover:bg-[#E48D57]/10 transition-colors"
                >
                  <span className="truncate">{opt}</span>
                  {value === opt && <Check size={14} className="text-[#E48D57] flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
